import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { trackPageTransition, getUserOnboardingState } from "utils/analytics";
import {
  trackAppcuesPageTransition,
  identifyUserForAppcues,
} from "utils/appcues";
import {
  SurfaceType,
  OnboardingState,
} from "utils/analytics/analyticsConstants";
import {
  useCurrentUserNoExcept,
  useCurrentSchoolId,
  useUserActivityState,
} from "helpers/useCurrentUser";
import { useGetHelpContext } from "components/GetHelpModal/GetHelpContext";

interface AnalyticsRouteWrapperProps {
  analyticsPageName: string; // should be snake_case, in the format "viewed_<page_name>_page"
  analyticsSurface?: SurfaceType;
  children: React.ReactNode;
}

export default function AnalyticsRouteWrapper({
  analyticsPageName,
  analyticsSurface = SurfaceType.NAVIGATION,
  children,
}: AnalyticsRouteWrapperProps) {
  const location = useLocation();
  const currentUser = useCurrentUserNoExcept();
  const schoolId = useCurrentSchoolId();
  const userActivityState = useUserActivityState();
  const { setHelpDeskLabel } = useGetHelpContext();

  let userOnboardingState: OnboardingState | undefined = undefined;
  if (currentUser) {
    userOnboardingState = getUserOnboardingState(currentUser);
    // Note not passing analytics ID because the Appcues method calculates it
    identifyUserForAppcues(
      currentUser.id,
      currentUser.type,
      schoolId,
      userOnboardingState,
    );
  }

  useEffect(() => {
    setHelpDeskLabel(analyticsPageName);
    const properties = {
      onboardingState: userOnboardingState,
      schoolId,
      userActivityState,
    };
    trackPageTransition(analyticsPageName, analyticsSurface, properties);
  }, [
    location,
    analyticsPageName,
    analyticsSurface,
    userOnboardingState,
    schoolId,
    userActivityState,
    setHelpDeskLabel,
  ]);

  useEffect(() => {
    trackAppcuesPageTransition();
  }, [location]);

  // Typescript doesn't like naked children returned
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
